import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";

const LETTERS = ["Alle", ..."#ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")];
const LIMIT = 48;

function BlueCheckmark() {
  return (
    <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 inline-flex"
      style={{
        background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 2px 8px rgba(59,130,246,0.5)",
      }}>
      <svg viewBox="0 0 12 12" fill="none" style={{ width: "7px", height: "7px" }}>
        <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );
}

function ArtistCard({ artist }) {
  return (
    <Link
      to={`/artist/${artist.slug}`}
      data-testid="artist-card"
      className="group flex flex-col items-center text-center gap-2.5 p-3 rounded-2xl hover:bg-zinc-900 transition-colors"
    >
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0">
        <div className="w-full h-full rounded-full overflow-hidden bg-zinc-800 ring-2 ring-transparent group-hover:ring-rose-500/50 transition-all duration-300">
          {artist.bild_url ? (
            <img src={artist.bild_url} alt={artist.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl sm:text-3xl font-black text-zinc-600 font-heading">
              {artist.name[0]?.toUpperCase()}
            </div>
          )}
        </div>
        {artist.linked_user_ids?.length > 0 && (
          <span className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
            style={{
              background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
              boxShadow: "0 1px 0 rgba(255,255,255,0.3) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 2px 8px rgba(59,130,246,0.55), 0 0 0 2px #050505",
            }}>
            <svg viewBox="0 0 12 12" fill="none" style={{ width: "8px", height: "8px" }}>
              <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </div>
      <div className="min-w-0 w-full">
        <p className="text-white text-xs sm:text-sm font-bold truncate group-hover:text-rose-300 transition-colors leading-snug">
          {artist.name}
        </p>
        {artist.release_count > 0 && (
          <p className="text-zinc-600 text-xs mt-0.5">{artist.release_count} Releases</p>
        )}
      </div>
    </Link>
  );
}

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [letter, setLetter] = useState("");
  const [page, setPage] = useState(1);

  const fetchArtists = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search) params.set("search", search);
      if (letter) params.set("letter", letter);
      const res = await api.get(`/artists/?${params}`);
      setArtists(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      setArtists([]);
    } finally {
      setLoading(false);
    }
  }, [search, letter, page]);

  useEffect(() => { fetchArtists(); }, [fetchArtists]);

  const handleLetter = (l) => {
    setLetter(l === "Alle" ? "" : l);
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
    setLetter("");
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Artist Datenbank | HUWND</title>
        <meta name="description" content="Alle Hands-Up-Artists auf einen Blick. Entdecke Diskografien, Kollaborationen und mehr." />
        <link rel="canonical" href="https://handsupwillneverdie.de/artists" />
        <meta property="og:title" content="Artist Datenbank | HUWND" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:description" content="Alle Hands-Up-Artists auf einen Blick." />
        <meta property="og:url" content="https://handsupwillneverdie.de/artists" />
      </Helmet>
      <Header />

      {/* ── PAGE HEADER ── */}
      <div className="pt-20 pb-8 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-2">Community</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black text-white font-heading">Artist Datenbank</h1>
              {total > 0 && (
                <p className="text-zinc-500 text-sm mt-2">{total.toLocaleString("de")} Artists</p>
              )}
            </div>
            {/* Search */}
            <div className="relative w-full sm:w-72">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                value={search}
                onChange={handleSearch}
                placeholder="Artist suchen…"
                data-testid="artists-search"
                className="w-full pl-9 pr-4 py-2.5 bg-zinc-900 border border-zinc-800 text-white text-sm rounded-xl focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── LETTER FILTER ── */}
      <div className="sticky top-0 z-20 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex gap-1 overflow-x-auto scrollbar-none">
          {LETTERS.map((l) => (
            <button
              key={l}
              onClick={() => handleLetter(l)}
              data-testid={`letter-filter-${l}`}
              className={`flex-shrink-0 min-w-[2rem] h-8 px-2 rounded-lg text-xs font-bold transition-colors ${
                (l === "Alle" && !letter) || letter === l
                  ? "bg-rose-600 text-white"
                  : "text-zinc-500 hover:text-white hover:bg-zinc-900"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* ── GRID ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : artists.length === 0 ? (
          <div className="text-center py-24">
            <Users size={44} className="mx-auto text-zinc-800 mb-4" />
            <p className="text-zinc-500 font-semibold">Keine Artists gefunden.</p>
            {search && (
              <button onClick={() => { setSearch(""); setLetter(""); }} className="mt-3 text-rose-500 text-sm hover:underline">
                Filter zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1">
            {artists.map((a) => (
              <ArtistCard key={a.id} artist={a} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && !loading && (
          <div className="flex items-center justify-center gap-3 mt-12">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-5 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 rounded-xl text-sm disabled:opacity-40 hover:border-rose-600/50 transition-colors"
            >
              Zurück
            </button>
            <span className="text-zinc-500 text-sm">
              {page} / {pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page === pages}
              className="px-5 py-2 bg-zinc-900 border border-zinc-800 text-zinc-400 rounded-xl text-sm disabled:opacity-40 hover:border-rose-600/50 transition-colors"
            >
              Weiter
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
